import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import {
  describeRecurrence,
  nowInZone,
  parseQuickAdd,
  parseRRule,
  type ReminderDTO,
  type ReminderInput,
  TASK_STATUS_NAMES,
  TASK_STATUSES,
  type TaskDetailDTO,
  type TaskDTO,
  MAX_TASK_DEPTH,
} from "@justone/shared";
import { errorMessage } from "../api/http";
import { useChannels, useMe, useThread, useUserMap, useUsers } from "../api/queries";
import { type UpdateTaskBody, useDeleteTask, useLabelMutations, useLabels, useLists, useSetReminders, useTask, useUpdateTask } from "../api/tasks";
import { formatDay, formatFull, messagePath } from "../lib/format";
import { channelView } from "../lib/room";
import { wikiPagePath } from "../lib/wiki";
import { formatClock, formatDue, PRIORITY_LABELS, PRIORITY_NAMES, recurrenceText } from "../lib/tasks";
import { type ComboOption, Combobox } from "./Combobox";
import { Composer } from "./Composer";
import { DatePicker } from "./DatePicker";
import { MessageBody } from "./MessageBody";
import { MessageRow } from "./MessageRow";
import { QuickAdd } from "./QuickAdd";
import { DueChip, TaskCheck, TaskRow, useOpenTask, useRestore, useToggleDone } from "./TaskBits";
import { Avatar, Dialog, Field, Icon, Skeleton, useDismiss, useToast } from "./ui";
import { useMenuKeys } from "./Popover";

/** 주소의 ?task=로 여는 할일 상세 (어느 화면에서나) */
export function TaskPanelHost() {
  const location = useLocation();
  const raw = new URLSearchParams(location.search).get("task");
  const id = raw && /^\d+$/.test(raw) ? Number(raw) : null;
  const open = useOpenTask();
  const close = useCallback(() => open(null), [open]);
  if (id === null) return null;
  return <TaskPanel key={id} id={id} onClose={close} />;
}

function TaskPanel({ id, onClose }: { id: number; onClose: () => void }) {
  const q = useTask(id);
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === "Escape" && !t.closest("input, textarea, select, dialog")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => panelRef.current?.focus(), []);

  return (
    <>
      <div className="task-scrim" onClick={onClose} />
      <aside className="task-panel" aria-label={`할일 T-${id}`} ref={panelRef} tabIndex={-1}>
        {q.isPending && <Skeleton kind="page" rows={6} />}
        {q.isError && (
          <div className="task-panel-body">
            <PanelHead id={id} onClose={onClose} />
            <p className="empty">할일을 열 수 없습니다. {errorMessage(q.error)}</p>
          </div>
        )}
        {q.data && <TaskDetail detail={q.data} onClose={onClose} />}
      </aside>
    </>
  );
}

function PanelHead({ id, onClose, children }: { id: number; onClose: () => void; children?: ReactNode }) {
  return (
    <header className="task-panel-head">
      <span className="task-no">T-{id}</span>
      <div className="crumbs">{children}</div>
      <button type="button" className="icon-btn" onClick={onClose} aria-label="할일 닫기" aria-keyshortcuts="Escape" data-tip="닫기" data-kbd="Esc">
        <Icon name="close" />
      </button>
    </header>
  );
}

function TaskDetail({ detail, onClose }: { detail: TaskDetailDTO; onClose: () => void }) {
  const { task, subtasks, ancestors, reminders, completions, source, wikiPages } = detail;
  const me = useMe();
  const users = useUserMap();
  const { data: channels = [] } = useChannels();
  const { data: lists = [] } = useLists();
  const update = useUpdateTask();
  const toast = useToast();
  const toggle = useToggleDone();
  const open = useOpenTask();
  const channel = task.channelId !== null ? channels.find((c) => c.id === task.channelId) : undefined;
  const team = task.channelId !== null;
  const canEdit = !task.deleted && (!team || (!!channel && ((channel.joined && (!channel.archived || me.role === "admin")) || task.assigneeId === me.id)));
  const save = (body: UpdateTaskBody) => update.mutate({ id: task.id, body }, { onError: (e) => toast(errorMessage(e)) });
  const depth = ancestors.length;
  const listName = task.listId !== null ? lists.find((l) => l.id === task.listId)?.name : null;

  return (
    <>
      <PanelHead id={task.id} onClose={onClose}>
        {team ? (
          <Link to={`/c/${task.channelId}/board`}>#{channel?.name ?? "채널"} 보드</Link>
        ) : (
          <Link to={task.listId !== null ? `/lists/${task.listId}` : "/inbox"}>{listName ?? "받은함"}</Link>
        )}
        {[...ancestors].reverse().map((a) => (
          <button key={a.id} type="button" className="crumb" onClick={() => open(a.id)}>
            {a.title}
          </button>
        ))}
        {canEdit && <TaskMenu task={task} />}
      </PanelHead>
      <div className="task-panel-body">
        {task.deleted && <DeletedNotice task={task} />}
        {!canEdit && !task.deleted && team && <p className="task-readonly">채널에 참여하면 이 할일을 바꿀 수 있습니다.</p>}

        <div className="task-title-row">
          <TaskCheck task={task} onToggle={toggle} disabled={!canEdit} />
          <TitleEditor task={task} disabled={!canEdit} onSave={(title) => save({ title })} />
        </div>

        {team && (
          <div className="status-seg" role="radiogroup" aria-label="상태">
            {TASK_STATUSES.map((s) => (
              <button key={s} type="button" role="radio" aria-checked={task.status === s} className={`st-${s}${task.status === s ? " on" : ""}`} disabled={!canEdit} onClick={() => task.status !== s && save({ status: s })}>
                {TASK_STATUS_NAMES[s]}
              </button>
            ))}
          </div>
        )}

        <dl className="task-props">
          {team && (
            <>
              <dt>담당자</dt>
              <dd>
                <AssigneePicker task={task} disabled={!canEdit} onPick={(assigneeId) => save({ assigneeId })} />
              </dd>
            </>
          )}
          <dt>마감</dt>
          <dd>
            <DueEditor task={task} disabled={!canEdit} onSave={save} />
          </dd>
          <dt>반복</dt>
          <dd>
            <RepeatEditor task={task} disabled={!canEdit} onSave={save} />
          </dd>
          <dt>우선순위</dt>
          <dd>
            <div className="prio-seg" role="radiogroup" aria-label="우선순위">
              {[1, 2, 3, 4].map((p) => (
                <button key={p} type="button" role="radio" aria-checked={task.priority === p} className={`prio-btn p${p}${task.priority === p ? " on" : ""}`} disabled={!canEdit} onClick={() => save({ priority: p })} title={PRIORITY_NAMES[p]}>
                  {PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          </dd>
          <dt>라벨</dt>
          <dd>
            <LabelPicker task={task} disabled={!canEdit} onSave={(labelIds) => save({ labelIds })} />
          </dd>
          {!team && depth === 0 && (
            <>
              <dt>목록</dt>
              <dd>
                <select className="input input-sm" value={task.listId ?? ""} disabled={!canEdit} onChange={(e) => save({ listId: e.target.value ? Number(e.target.value) : null })} aria-label="목록">
                  <option value="">받은함</option>
                  {lists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </dd>
            </>
          )}
          <dt>리마인더</dt>
          <dd>
            <ReminderEditor task={task} reminders={reminders} />
          </dd>
        </dl>

        <DescriptionEditor task={task} disabled={!canEdit} onSave={(description) => save({ description })} />

        <section className="task-section" aria-label="하위 할일">
          <h3>
            하위 할일 {task.subtaskCount > 0 && <span className="muted">{task.subtaskDone}/{task.subtaskCount}</span>}
          </h3>
          {subtasks.map((s) => (
            <TaskRow key={s.id} task={s} users={users} showPlace={false} depth={depthOf(s, task.id, subtasks)} />
          ))}
          {canEdit && depth < MAX_TASK_DEPTH && <QuickAdd inline defaults={{ parentId: task.id, channelId: task.channelId ?? undefined }} placeholder="하위 할일 추가" onDone={() => {}} />}
        </section>

        {completions.length > 0 && (
          <section className="task-section" aria-label="완료 기록">
            <h3>완료 기록</h3>
            <ul className="completions">
              {completions.map((c) => (
                <li key={c.completedAt}>
                  {c.dueDate && <span>{formatDue({ dueDate: c.dueDate, dueTime: null })} 회차</span>} · {users.get(c.userId ?? 0)?.displayName ?? "알 수 없음"} · {formatFull(c.completedAt)}
                </li>
              ))}
            </ul>
          </section>
        )}

        {source && (
          <section className="task-section" aria-label="출처 메시지">
            <h3>출처 메시지</h3>
            <Link className="source-link" to={messagePath({ channelId: source.channelId, conversationId: null }, source.messageId, null)}>
              <Avatar user={users.get(source.authorId ?? 0)} size={20} />
              <span>
                #{channels.find((c) => c.id === source.channelId)?.name ?? "채널"} · {source.preview || "(내용 없음)"}
              </span>
            </Link>
          </section>
        )}

        {wikiPages.length > 0 && (
          <section className="task-section" aria-label="위키">
            <h3>위키</h3>
            <ul className="wiki-refs">
              {wikiPages.map((w) => (
                <li key={w.pageId}>
                  <Link className="source-link" to={wikiPagePath(w.channelId, w.pageId)}>
                    <span>{w.title}</span>
                  </Link>
                  <span className="muted"> · 체크하면 함께 끝납니다</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="task-foot">
          {users.get(task.creatorId ?? 0)?.displayName ?? "알 수 없음"}님이 {formatDay(task.createdAt)}에 만들었습니다.
          {task.completedAt && <> 완료: {formatFull(task.completedAt)}</>}
        </p>

        {team && task.cardMessageId && channel && <Comments cardMessageId={task.cardMessageId} channel={channel} />}
      </div>
    </>
  );
}

function depthOf(t: TaskDTO, rootId: number, all: TaskDTO[]): number {
  let d = 0;
  let cur: TaskDTO | undefined = t;
  while (cur && cur.parentId !== rootId && d < MAX_TASK_DEPTH) {
    cur = all.find((x) => x.id === cur!.parentId);
    d++;
  }
  return d;
}

function DeletedNotice({ task }: { task: TaskDTO }) {
  const restore = useRestore();
  return (
    <div className="move-banner" role="status">
      <span>지운 할일입니다.</span>
      <button type="button" className="btn btn-sm" onClick={() => restore(task)}>
        되살리기
      </button>
    </div>
  );
}

function TaskMenu({ task }: { task: TaskDTO }) {
  const [open, setOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss<HTMLDivElement>(open, close);
  useMenuKeys(open, ref);
  const del = useDeleteTask();
  const toast = useToast();
  const restore = useRestore();
  const openTask = useOpenTask();
  const remove = () => {
    close();
    del.mutate(task, {
      onSuccess: () => {
        restore(task);
        openTask(task.parentId);
      },
      onError: (e) => toast(errorMessage(e)),
    });
  };
  const copy = () => {
    close();
    const url = `${location.origin}${task.channelId !== null ? `/c/${task.channelId}/board` : "/today"}?task=${task.id}`;
    void navigator.clipboard?.writeText(url).then(() => toast("링크를 복사했습니다."));
  };
  return (
    <div className="task-menu" ref={ref}>
      <button type="button" className="icon-btn" onClick={() => setOpen((o) => !o)} aria-label="할일 메뉴" aria-haspopup="menu" aria-expanded={open}>
        <Icon name="more" />
      </button>
      {open && (
        <div className="menu" role="menu">
          {task.channelId === null && task.parentId === null && (
            <button type="button" role="menuitem" onClick={() => (close(), setMoving(true))}>
              채널로 옮기기 (팀 할일로)
            </button>
          )}
          <button type="button" role="menuitem" onClick={copy}>
            링크 복사
          </button>
          <button type="button" role="menuitem" className="btn-danger" onClick={remove}>
            지우기
          </button>
        </div>
      )}
      {moving && <MoveToChannelDialog task={task} onClose={() => setMoving(false)} />}
    </div>
  );
}

/** 개인 할일 → 팀 할일 (공개된다는 확인 후) */
function MoveToChannelDialog({ task, onClose }: { task: TaskDTO; onClose: () => void }) {
  const { data: channels = [] } = useChannels();
  const { data: labels = [] } = useLabels();
  const targets = channels.filter((c) => c.joined && !c.archived);
  const [channelId, setChannelId] = useState(targets.find((c) => c.isDefault)?.id ?? targets[0]?.id ?? 0);
  const [silent, setSilent] = useState(false);
  const update = useUpdateTask();
  const toast = useToast();
  const personalLabels = task.labelIds.filter((id) => labels.find((l) => l.id === id)?.scope === "personal");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate(
      { id: task.id, body: { channelId, silent } },
      {
        onSuccess: () => {
          toast("팀 할일로 옮겼습니다.");
          onClose();
        },
        onError: (err) => toast(errorMessage(err)),
      },
    );
  };
  return (
    <Dialog open onClose={onClose} label="채널로 옮기기">
      <form onSubmit={submit}>
        <h2>채널로 옮기기</h2>
        <p style={{ marginTop: 0, color: "var(--ink-2)" }}>
          팀 할일이 되면 <strong>모든 멤버가 볼 수 있고</strong>, 다시 개인 할일로 되돌릴 수 없습니다. 하위 할일도 함께 옮겨집니다.
          {personalLabels.length > 0 && <> 개인 라벨 {personalLabels.length}개는 떨어집니다.</>}
        </p>
        <Field id="move-task-channel" label="채널">
          <select id="move-task-channel" className="input" value={channelId} onChange={(e) => setChannelId(Number(e.target.value))}>
            {targets.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.name}
              </option>
            ))}
          </select>
        </Field>
        <label className="qa-option">
          <input type="checkbox" checked={silent} onChange={(e) => setSilent(e.target.checked)} />
          조용히 올리기 (채널 알림 없이)
        </label>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={!channelId || update.isPending}>
            옮기기
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function TitleEditor({ task, disabled, onSave }: { task: TaskDTO; disabled: boolean; onSave: (title: string) => void }) {
  const [value, setValue] = useState(task.title);
  useEffect(() => setValue(task.title), [task.title]);
  const commit = () => {
    const v = value.trim();
    if (!v) setValue(task.title);
    else if (v !== task.title) onSave(v);
  };
  return (
    <textarea
      className={`task-title-input${task.status === "done" ? " is-done" : ""}`}
      value={value}
      rows={1}
      disabled={disabled}
      aria-label="제목"
      maxLength={300}
      onChange={(e) => setValue(e.target.value.replace(/\n/g, " "))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.nativeEvent.isComposing && e.keyCode !== 229) {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
        if (e.key === "Escape") {
          setValue(task.title);
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      ref={(el) => {
        if (el) {
          el.style.height = "auto";
          el.style.height = `${el.scrollHeight}px`;
        }
      }}
    />
  );
}

function AssigneePicker({ task, disabled, onPick }: { task: TaskDTO; disabled: boolean; onPick: (id: number | null) => void }) {
  const { data: users = [] } = useUsers();
  const me = useMe();
  const options: ComboOption<number | null>[] = [
    { value: null, label: "담당자 없음" },
    ...users
      .filter((u) => u.active || u.id === task.assigneeId)
      .sort((a, b) => Number(b.id === me.id) - Number(a.id === me.id) || a.displayName.localeCompare(b.displayName, "ko"))
      .map((u) => ({ value: u.id, label: u.displayName, keywords: u.username ?? "", hint: u.id === me.id ? "나" : u.username ? `@${u.username}` : undefined, icon: <Avatar user={u} size={22} presence /> })),
  ];
  return (
    <div className="assignee-pick">
      <Combobox
        label="담당자"
        value={task.assigneeId}
        options={options}
        onChange={onPick}
        disabled={disabled}
        placeholder="이름이나 아이디 (초성도 됩니다)"
        renderValue={(o) => (
          <>
            {o?.icon}
            <span className={o?.value === null ? "muted" : undefined}>{o?.label ?? "담당자 없음"}</span>
          </>
        )}
      />
      {task.assigneeId !== me.id && !disabled && (
        <button type="button" className="btn btn-sm btn-quiet" onClick={() => onPick(me.id)}>
          내가 맡기
        </button>
      )}
    </div>
  );
}

/** 마감: 날짜·시각 직접 고르기 또는 말로 ("다음주 금요일 오후 3시") */
function DueEditor({ task, disabled, onSave }: { task: TaskDTO; disabled: boolean; onSave: (b: UpdateTaskBody) => void }) {
  const [text, setText] = useState("");
  const [hint, setHint] = useState<string | null>(null);
  const now = nowInZone();
  const parsed = text.trim() ? parseQuickAdd(text, { today: now.date, nowMinutes: now.minutes, isTarget: () => false, isUser: () => false }) : null;
  const preview = parsed?.dueDate ? formatDue({ dueDate: parsed.dueDate, dueTime: parsed.dueTime }) : null;
  const apply = (e: FormEvent) => {
    e.preventDefault();
    if (!parsed?.dueDate) {
      setHint("알아들을 수 없는 날짜입니다. 예: 내일, 금요일 오후 3시, 10/20");
      return;
    }
    onSave({ dueDate: parsed.dueDate, dueTime: parsed.dueTime });
    setText("");
    setHint(null);
  };
  return (
    <div className="due-editor">
      <div className="row">
        <DatePicker label="마감 날짜" value={task.dueDate} disabled={disabled} onChange={(d) => onSave({ dueDate: d })} />
        <input type="time" className="input input-sm" value={task.dueTime ?? ""} disabled={disabled || !task.dueDate} aria-label="마감 시각" onChange={(e) => onSave({ dueTime: e.target.value || null })} />
        {task.dueDate && !disabled && (
          <button type="button" className="btn btn-sm btn-quiet" onClick={() => onSave({ dueDate: null })}>
            지우기
          </button>
        )}
      </div>
      {!disabled && (
        <form onSubmit={apply} className="row">
          <input className="input input-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="말로 쓰기: 다음주 금요일 오후 3시" aria-label="마감을 말로 쓰기" />
          {preview && <span className="muted">→ {preview}</span>}
          {text.trim() && (
            <button type="submit" className="btn btn-sm">
              적용
            </button>
          )}
        </form>
      )}
      {hint && <span className="form-error">{hint}</span>}
    </div>
  );
}

function RepeatEditor({ task, disabled, onSave }: { task: TaskDTO; disabled: boolean; onSave: (b: UpdateTaskBody) => void }) {
  const [text, setText] = useState("");
  const [hint, setHint] = useState<string | null>(null);
  const now = nowInZone();
  const parsed = text.trim() ? parseQuickAdd(text, { today: now.date, nowMinutes: now.minutes, isTarget: () => false, isUser: () => false }) : null;
  const rule = parsed?.rrule ? parseRRule(parsed.rrule) : null;
  const current = recurrenceText(task);
  const apply = (e: FormEvent) => {
    e.preventDefault();
    if (!parsed?.rrule) {
      setHint("알아들을 수 없는 반복입니다. 예: 매일, 매주 월수금, 격주 금요일, 매달 1일, 매 3일(완료 후)");
      return;
    }
    onSave({ rrule: parsed.rrule, repeatFromCompletion: parsed.repeatFromCompletion, ...(task.dueDate ? {} : { dueDate: parsed.dueDate }) });
    setText("");
    setHint(null);
  };
  return (
    <div className="due-editor">
      <div className="row">
        <span>{current ?? "반복 안 함"}</span>
        {current && !disabled && (
          <button type="button" className="btn btn-sm btn-quiet" onClick={() => onSave({ rrule: null })}>
            반복 끄기
          </button>
        )}
      </div>
      {!disabled && (
        <form onSubmit={apply} className="row">
          <input className="input input-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="예: 매주 월요일, 매달 마지막 날" aria-label="반복 규칙을 말로 쓰기" />
          {rule && <span className="muted">→ {describeRecurrence(rule, parsed!.repeatFromCompletion)}</span>}
          {text.trim() && (
            <button type="submit" className="btn btn-sm">
              적용
            </button>
          )}
        </form>
      )}
      {hint && <span className="form-error">{hint}</span>}
    </div>
  );
}

function LabelPicker({ task, disabled, onSave }: { task: TaskDTO; disabled: boolean; onSave: (ids: number[]) => void }) {
  const { data: labels = [] } = useLabels();
  const ops = useLabelMutations();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss<HTMLDivElement>(open, close);
  const team = task.channelId !== null;
  const usable = labels.filter((l) => !team || l.scope === "shared");
  const shown = usable.filter((l) => !query || l.name.includes(query.replace(/^\+/, "")));
  const toggle = (id: number) => onSave(task.labelIds.includes(id) ? task.labelIds.filter((x) => x !== id) : [...task.labelIds, id]);
  const name = query.trim().replace(/^\+/, "");
  const createNew = (scope: "shared" | "personal") =>
    ops.create.mutate(
      { name, scope },
      {
        onSuccess: (l) => {
          onSave([...task.labelIds, l.id]);
          setQuery("");
        },
        onError: (e) => toast(errorMessage(e)),
      },
    );
  return (
    <div className="label-pick" ref={ref}>
      {task.labelIds.map((id) => {
        const l = labels.find((x) => x.id === id);
        return l ? (
          <span key={id} className={`label-chip lc-${l.color}`}>
            {l.name}
            {!disabled && (
              <button type="button" onClick={() => toggle(id)} aria-label={`${l.name} 라벨 떼기`}>
                ×
              </button>
            )}
          </span>
        ) : null;
      })}
      {!disabled && (
        <button type="button" className="btn btn-sm btn-quiet" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          + 라벨
        </button>
      )}
      {open && (
        <div className="menu label-menu" role="dialog" aria-label="라벨 고르기">
          <input className="input input-sm" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="라벨 찾기 또는 새로 만들기" autoFocus aria-label="라벨 찾기" />
          <div className="label-options">
            {shown.map((l) => (
              <label key={l.id}>
                <input type="checkbox" checked={task.labelIds.includes(l.id)} onChange={() => toggle(l.id)} />
                <span className={`label-chip lc-${l.color}`}>{l.name}</span>
                <span className="muted">{l.scope === "shared" ? "공용" : "개인"}</span>
              </label>
            ))}
            {shown.length === 0 && !name && <span className="muted">라벨이 없습니다.</span>}
          </div>
          {name && !usable.some((l) => l.name === name) && /^[^\s#@+!]+$/.test(name) && (
            <div className="row">
              <button type="button" className="btn btn-sm" onClick={() => createNew("shared")}>
                공용 라벨 +{name} 만들기
              </button>
              {!team && (
                <button type="button" className="btn btn-sm" onClick={() => createNew("personal")}>
                  개인 라벨로
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const REMINDER_PRESETS: { key: string; label: string; input: ReminderInput; needs: "time" | "date" }[] = [
  { key: "b0", label: "마감 시각에", input: { kind: "before", offsetMinutes: 0 }, needs: "time" },
  { key: "b10", label: "10분 전", input: { kind: "before", offsetMinutes: 10 }, needs: "time" },
  { key: "b30", label: "30분 전", input: { kind: "before", offsetMinutes: 30 }, needs: "time" },
  { key: "b60", label: "1시간 전", input: { kind: "before", offsetMinutes: 60 }, needs: "time" },
  { key: "d0", label: "그날 오전 9시", input: { kind: "day_before", daysBefore: 0, time: "09:00" }, needs: "date" },
  { key: "d1", label: "하루 전 오전 9시", input: { kind: "day_before", daysBefore: 1, time: "09:00" }, needs: "date" },
];

function toInput(r: ReminderDTO): ReminderInput {
  if (r.kind === "before") return { kind: "before", offsetMinutes: r.offsetMinutes ?? 0 };
  if (r.kind === "day_before") return { kind: "day_before", daysBefore: r.daysBefore ?? 0, time: r.time ?? "09:00" };
  return { kind: "at", at: r.at! };
}

function describeReminder(r: ReminderDTO): string {
  if (r.kind === "before") return r.offsetMinutes ? (r.offsetMinutes % 60 === 0 ? `${r.offsetMinutes / 60}시간 전` : `${r.offsetMinutes}분 전`) : "마감 시각에";
  if (r.kind === "day_before") return `${r.daysBefore ? `${r.daysBefore}일 전` : "그날"} ${formatClock(r.time ?? "09:00")}`;
  return formatFull(r.at!);
}

/** 내 리마인더 (팀 할일에도 사람마다 따로) */
function ReminderEditor({ task, reminders }: { task: TaskDTO; reminders: ReminderDTO[] }) {
  const set = useSetReminders(task.id);
  const toast = useToast();
  const [custom, setCustom] = useState("");
  const bypass = reminders.some((r) => r.bypassQuiet);
  const replace = (list: ReminderInput[], bypassQuiet = bypass) => set.mutate({ reminders: list, bypassQuiet }, { onError: (e) => toast(errorMessage(e)) });
  const current = reminders.map(toInput);
  const available = REMINDER_PRESETS.filter((p) => (p.needs === "time" ? !!task.dueTime : !!task.dueDate) && !current.some((c) => JSON.stringify(c) === JSON.stringify(p.input)));
  return (
    <div className="reminders">
      {reminders.length === 0 && <span className="muted">{task.dueTime ? "없음" : "마감 시각을 정하면 그때 알려드립니다."}</span>}
      {reminders.map((r, i) => (
        <span key={r.id} className={`reminder-chip${r.sent ? " sent" : ""}${!r.fireAt ? " idle" : ""}`} title={r.fireAt ? `${formatFull(r.fireAt)}${r.sent ? " (보냄)" : ""}` : "마감 시각이 없어 보낼 수 없습니다"}>
          <Icon name="bell" />
          {describeReminder(r)}
          <button type="button" onClick={() => replace(current.filter((_, j) => j !== i))} aria-label={`${describeReminder(r)} 리마인더 지우기`}>
            ×
          </button>
        </span>
      ))}
      <div className="row">
        {available.length > 0 && (
          <select
            className="input input-sm"
            value=""
            aria-label="리마인더 추가"
            onChange={(e) => {
              const p = REMINDER_PRESETS.find((x) => x.key === e.target.value);
              if (p) replace([...current, p.input]);
            }}
          >
            <option value="">+ 리마인더</option>
            {available.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        )}
        <input type="datetime-local" className="input input-sm" value={custom} onChange={(e) => setCustom(e.target.value)} aria-label="정한 시각에 알림" />
        {custom && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              replace([...current, { kind: "at", at: new Date(custom).toISOString() }]);
              setCustom("");
            }}
          >
            추가
          </button>
        )}
      </div>
      {reminders.length > 0 && (
        <label className="qa-option switch-label" title="내 계정의 방해 금지 시간에도 이 할일의 리마인더는 푸시로 보냅니다">
          <input type="checkbox" role="switch" className="switch" checked={bypass} onChange={(e) => replace(current, e.target.checked)} />
          방해 금지 시간에도 알림
        </label>
      )}
    </div>
  );
}

function DescriptionEditor({ task, disabled, onSave }: { task: TaskDTO; disabled: boolean; onSave: (d: string) => void }) {
  const me = useMe();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(task.description);
  const meNames = useMemo(() => new Set([me.username, me.displayName].filter((x): x is string => !!x).map((x) => x.toLowerCase())), [me]);
  useEffect(() => {
    if (!editing) setValue(task.description);
  }, [task.description, editing]);
  const commit = () => {
    setEditing(false);
    if (value !== task.description) onSave(value);
  };
  if (editing)
    return (
      <div className="task-desc">
        <label htmlFor={`desc-${task.id}`} className="sr-only">
          설명
        </label>
        <textarea
          id={`desc-${task.id}`}
          className="input"
          value={value}
          rows={Math.max(4, value.split("\n").length + 1)}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setValue(task.description);
              setEditing(false);
            }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
          }}
          autoFocus
          placeholder="마크다운으로 쓸 수 있습니다"
        />
        <span className="muted">Ctrl+Enter 또는 바깥을 누르면 저장, Esc는 취소</span>
      </div>
    );
  return (
    <button type="button" className="task-desc view" onClick={() => !disabled && setEditing(true)} disabled={disabled} aria-label="설명 고치기">
      {task.description ? <MessageBody body={task.description} me={meNames} /> : <span className="muted">{disabled ? "설명 없음" : "설명 추가…"}</span>}
    </button>
  );
}

/** 팀 할일 댓글 = 채널 카드 메시지의 스레드 */
function Comments({ cardMessageId, channel }: { cardMessageId: number; channel: NonNullable<ReturnType<typeof useChannels>["data"]>[number] }) {
  const q = useThread(cardMessageId);
  const me = useMe();
  const users = useUserMap();
  const room = channelView(channel, me);
  const meNames = useMemo(() => new Set([me.username, me.displayName].filter((x): x is string => !!x).map((x) => x.toLowerCase())), [me]);
  const replies = q.data?.replies ?? [];
  return (
    <section className="task-section task-comments" aria-label="댓글">
      <h3>
        댓글과 기록 <Link to={`/c/${channel.id}/t/${cardMessageId}`} className="muted">채널 스레드에서 보기</Link>
      </h3>
      {replies.length === 0 && <p className="muted">아직 댓글이 없습니다.</p>}
      {replies.map((r, i) => {
        const before = replies[i - 1];
        const continued = !!before && before.authorId === r.authorId && before.kind === r.kind && new Date(r.createdAt).getTime() - new Date(before.createdAt).getTime() < 5 * 60_000;
        return <MessageRow key={r.id} message={r} users={users} me={me} meNames={meNames} continued={continued} />;
      })}
      {room.canWrite ? <Composer key={`task-comments-${cardMessageId}`} room={room} parentId={cardMessageId} placeholder="댓글 달기" autoFocus={false} /> : <div className="composer-blocked">채널에 참여해야 댓글을 달 수 있습니다.</div>}
    </section>
  );
}
